import { MicroSkillsProps, MicroSkillTableDatumType } from './types';

type Props<T extends object> = Pick<
  MicroSkillsProps<T>,
  'microSkillsMap' | 'rootMicroSkillIds' | 'hierarchyMicroSkillsMap' | 'canAddMicroSkill'
>;

export const MICRO_SKILL_LAZY_LOAD_ID = 'lazy-load';

export const checkIfMicroSkillLazyLoaded = (childrenMicroSkillIds?: string[]) =>
  childrenMicroSkillIds?.length && childrenMicroSkillIds[0] === MICRO_SKILL_LAZY_LOAD_ID;

export const adaptMicroSkillDatum = <T extends object>({
  microSkillsMap,
  hierarchyMicroSkillsMap,
  rootMicroSkillIds,
  canAddMicroSkill,
}: Props<T>) => {
  const data: MicroSkillTableDatumType[] = [];

  const adaptMicroSkillChildren = ({
    parentMicroSkillId,
  }: {
    parentMicroSkillId: string;
  }): MicroSkillTableDatumType[] => {
    const childrenMicroSkillIds = hierarchyMicroSkillsMap[parentMicroSkillId];
    const isLazyLoaded = checkIfMicroSkillLazyLoaded(childrenMicroSkillIds);

    if (isLazyLoaded) {
      return [
        {
          id: `lazy-load-${parentMicroSkillId}`,
          type: 'LAZY_LOAD',
          parentMicroSkillId,
        },
      ];
    }

    return (
      childrenMicroSkillIds?.reduce((accum, childrenMicroSkillId) => {
        const childrenMicroSkill = microSkillsMap[childrenMicroSkillId];

        if (!childrenMicroSkill) return accum;

        return [
          ...accum,
          {
            id: childrenMicroSkillId,
            type: 'MICRO_SKILL',
            microSkill: childrenMicroSkill,
            children: adaptMicroSkillChildren({ parentMicroSkillId: childrenMicroSkillId }),
            parentId: parentMicroSkillId,
          },
        ];
      }, [] as MicroSkillTableDatumType[]) ?? ([] as MicroSkillTableDatumType[])
    );
  };

  const rootMicroSkills: MicroSkillTableDatumType[] = rootMicroSkillIds.reduce(
    (accum, rootMicroSkillId) => {
      const rootMicroSkill = microSkillsMap[rootMicroSkillId];
      if (!rootMicroSkill) return accum;

      return [
        ...accum,
        {
          id: rootMicroSkillId,
          type: 'MICRO_SKILL',
          microSkill: rootMicroSkill,
          children: adaptMicroSkillChildren({ parentMicroSkillId: rootMicroSkillId }),
        },
      ];
    },
    [] as MicroSkillTableDatumType[],
  );

  rootMicroSkills.forEach((rootMicroSkill) => {
    data.push(rootMicroSkill);
  });

  if (canAddMicroSkill) {
    data.push({ id: 'add-micro-skill', type: 'ACTION' });
  }

  return data;
};
